<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { storeToRefs } from 'pinia';
  import { usePrintingStore } from '@/composition/use-printing';
  import { useNativeJobActions } from '@/composition/printing/use-native-job-actions';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { formatJobTime } from '@/composition/printing/use-printing-v2-ui';
  import { IPrintJob } from '@/interfaces/printing.interfaces';
  import { PrinterType } from '@/modules/PrintService';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const store = usePrintingStore();
  const { lastPrintJobs } = storeToRefs(store);
  const actions = useNativeJobActions();
  const modals = useModal();
  const ui = useUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const jobs = computed<IPrintJob[]>(() => lastPrintJobs.value);

  function jobIcon(job: IPrintJob): string {
    switch (job.printerType) {
      case PrinterType.Comanda:
        return 'silverware-fork-knife';
      case PrinterType.Etiqueta:
        return 'tag-outline';
      case PrinterType.Ticket:
      default:
        return 'receipt';
    }
  }

  function jobKindLabel(job: IPrintJob): string {
    switch (job.printerType) {
      case PrinterType.Comanda:
        return i18nT('printingV2.kindComanda');
      case PrinterType.Etiqueta:
        return i18nT('printingV2.kindSticker');
      case PrinterType.Ticket:
      default:
        return i18nT('printingV2.kindTicket');
    }
  }

  function jobTime(job: IPrintJob): string {
    return formatJobTime(new Date(job.createdAt).getTime());
  }

  interface IStatusUi {
    color: 'success' | 'error' | 'warning';
    icon: string;
    labelKey: string;
  }

  function statusUi(job: IPrintJob): IStatusUi {
    if (job.status === 'printed') {
      return { color: 'success', icon: 'check', labelKey: 'printingV2.jobPrinted' };
    }
    if (job.status === 'unknown') {
      // Post-send DLE-EOT status report: the document MAY have printed.
      return {
        color: 'warning',
        icon: 'help',
        labelKey: 'printingV2.jobVerify',
      };
    }
    return { color: 'error', icon: 'close', labelKey: 'printingV2.jobFailed' };
  }

  async function resubmit(job: IPrintJob, isRetry: boolean) {
    if (actions.busyJobIds.value.includes(job.jobId)) return;

    if (!isRetry && actions.needsReprintConfirm(job)) {
      const accepted = await modals.confirm({
        title: i18nT('printingV2.reprintConfirmTitle'),
        message: i18nT('printingV2.reprintConfirmBody'),
        acceptText: i18nT('printingV2.reprint'),
      });
      if (!accepted) return;
    }

    try {
      await actions.resubmit(job);
      ui.showToast(
        AlertType.Success,
        i18nT(isRetry ? 'printingV2.retrySent' : 'printingV2.reprintSent')
      );
    } catch (e) {
      console.error('[NativePrinting] resubmit failed', e);
      ui.showToast(AlertType.Error, i18nT('printingV2.actionFailed'));
    }
  }
</script>

<template>
  <div class="v2-native-jobs-tab">
    <PosUiEmptyState
      v-if="!jobs.length"
      icon="history"
      :title="i18nT('printingV2.jobsEmptyTitle')"
      :description="i18nT('printingV2.jobsEmptyDescription')"
    />

    <div v-else class="jobs-list">
      <div v-for="job in jobs" :key="job.jobId" class="job-row">
        <div class="job-kind-icon">
          <PosUiIcon :name="jobIcon(job)" />
        </div>

        <div class="job-info">
          <div class="job-label">{{ job.title }}</div>
          <div class="job-detail">
            {{ jobTime(job) }} · {{ jobKindLabel(job) }}
            <template v-if="job.printerName"> · {{ job.printerName }}</template>
            <template v-if="job.subtitle"> · {{ job.subtitle }}</template>
          </div>
          <div
            v-if="job.status !== 'printed' && job.errorMessage"
            class="job-error"
            :class="{ 'is-verify': job.status === 'unknown' }"
          >
            {{ job.errorMessage }}
          </div>
        </div>

        <div class="job-side">
          <PosUiBadge
            :color="statusUi(job).color"
            variant="soft"
            size="sm"
            :icon="statusUi(job).icon"
          >
            {{ $t(statusUi(job).labelKey) }}
          </PosUiBadge>

          <div class="job-actions">
            <PosUiButton
              v-if="actions.canRetry(job)"
              size="xs"
              color="primary"
              variant="soft"
              icon="refresh"
              :loading="actions.busyJobIds.value.includes(job.jobId)"
              @click="resubmit(job, true)"
            >
              {{ $t('printingV2.retry') }}
            </PosUiButton>
            <PosUiButton
              v-else-if="actions.canReprint(job)"
              size="xs"
              color="neutral"
              variant="outline"
              icon="printer"
              :loading="actions.busyJobIds.value.includes(job.jobId)"
              @click="resubmit(job, false)"
            >
              {{ $t('printingV2.reprint') }}
            </PosUiButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-native-jobs-tab {
    .jobs-list {
      @apply tw-flex tw-flex-col tw-gap-2;

      .job-row {
        @apply tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-3;

        .job-kind-icon {
          @apply tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-100 tw-text-lg tw-text-neutral-500;
        }

        .job-info {
          @apply tw-min-w-0 tw-flex-1;

          .job-label {
            @apply tw-truncate tw-text-sm tw-font-medium tw-text-neutral-800;
          }

          .job-detail {
            @apply tw-text-xs tw-text-neutral-500;
          }

          .job-error {
            @apply tw-mt-0.5 tw-text-xs tw-text-red-600;

            &.is-verify {
              @apply tw-text-amber-600;
            }
          }
        }

        .job-side {
          @apply tw-flex tw-flex-none tw-flex-col tw-items-end tw-gap-1.5;

          .job-actions {
            @apply tw-flex tw-gap-1;
          }
        }
      }
    }
  }
</style>
